import { cn } from '@ifixit/ui/lib/utils';
import { SearchResultItem } from '../../types';
import { SearchResultCard } from '../result-card';
import { SectionHeader } from './section-header';
import { type LocaleCode, useTranslations } from '@ifixit/i18n';
import { Doctype } from '@ifixit/helpers';

interface SearchResultSectionProps {
   doctype: Doctype;
   hits: SearchResultItem[];
   totalHits: number;
   query: string;
   className?: string;
   currency: string;
   localeCode: LocaleCode;
   storeCode: string;
}

/**
 * Get the translated title for a doctype
 */
function useDoctypeTitle(doctype: Doctype): string {
   const t = useTranslations('Search.sections');
   switch (doctype) {
      case 'product': {
         return t('product');
      }
      case 'topic': {
         return t('topic');
      }
      case 'guide': {
         return t('guide');
      }
      case 'wiki': {
         return t('wiki');
      }
      case 'post': {
         return t('post');
      }
      case 'document': {
         return t('document');
      }
      case 'news': {
         return t('news');
      }
      case 'pages': {
         return t('searchpage');
      }
      default: {
         return doctype;
      }
   }
}

/**
 * Search result cards container component
 */
function ResultCards({
   hits,
   currency,
   localeCode,
   storeCode,
}: {
   hits: SearchResultItem[];
   currency: string;
   localeCode: LocaleCode;
   storeCode: string;
}) {
   return (
      <div className="flex flex-col items-start self-stretch">
         {hits.map((hit, key) => (
            <SearchResultCard
               key={hit.objectID || key}
               hit={hit}
               currency={currency}
               localeCode={localeCode}
               storeCode={storeCode}
               position={key}
            />
         ))}
      </div>
   );
}

/**
 * Main search result section component that renders a category of search results
 */
export function SearchResultSection({
   doctype,
   hits,
   totalHits,
   query,
   className,
   currency,
   localeCode,
   storeCode,
}: SearchResultSectionProps) {
   const title = useDoctypeTitle(doctype);

   // Don't render anything if there are no results
   if (hits.length === 0) return null;

   return (
      <div className={cn('flex flex-col items-start self-stretch', className)}>
         <SectionHeader
            title={title}
            query={query}
            doctype={doctype}
            totalHits={totalHits}
            localeCode={localeCode}
         />
         <ResultCards
            hits={hits}
            currency={currency}
            localeCode={localeCode}
            storeCode={storeCode}
         />
      </div>
   );
}
